//
// Imports
//

import
{
	getElementOrThrow,
	getIntegerData,
	getIntegerDataOrThrow,
	getStringData,
	getStringDataOrThrow,
} from "@lorenstuff/browser-utilities";
import { DE } from "@lorenstuff/document-builder";

import { Runtime } from "../../../../_shared/classes/Runtime.js";

import { Anchor } from "../anchors/Anchor.js";

//
// Locals
//

type PlayInfo =
{
	work:
	{
		id: string;
		title: string;
		authors:
		{
			id: string;
			name: string;
		}[];
		totalChapters: number | null;
	};
	chapter:
	{
		title: string | null;
		number: number;
		runtime: Runtime;
		stream:
		{
			url: string;
		};
	};
};

let play: (playInfo: PlayInfo) => void;

async function initialise(element: HTMLElement)
{
	const audio = getElementOrThrow<HTMLAudioElement>(element, "audio");

	const seekbar = getElementOrThrow(element, ".seekbar");
	const seekbarFill = getElementOrThrow(seekbar, ".fill");

	const wrapper = getElementOrThrow(element, ".wrapper");

	const metaSection = getElementOrThrow(wrapper, ".section.meta");
	const workTitleElement = getElementOrThrow(metaSection, ".work-title");
	const workAuthorsElement = getElementOrThrow(metaSection, ".work-authors");
	const chapterNumberAndTitleElement = getElementOrThrow(metaSection, ".chapter-number-and-title");

	const controlsSection = getElementOrThrow(wrapper, ".section.controls");
	const back10sControl = getElementOrThrow(controlsSection, `[data-name="back-10s"]`);
	const playControl = getElementOrThrow(controlsSection, `[data-name="play"]`);
	const playControlIcon = getElementOrThrow(playControl, "span");
	const forward10sControl = getElementOrThrow(controlsSection, `[data-name="forward-10s"`);

	const timeSection = getElementOrThrow(wrapper, ".section.time");
	const elapsedTime = getElementOrThrow(timeSection, ".elapsed");
	const durationTime = getElementOrThrow(timeSection, ".duration");

	const updateSeekbar = () =>
	{
		// Note: audio.duration will be NaN and not null if there is no audio
		//	because web standards people are assholes
		const percentage = !isNaN(audio.duration)
			? (audio.currentTime / audio.duration) * 100
			: 0;

		seekbarFill.style.width = percentage + "%";
	};

	audio.addEventListener("ended", () =>
	{
		audio.currentTime = 0;

		audio.pause();

		updateSeekbar();

		playControlIcon.classList.remove("fa-pause");
		playControlIcon.classList.add("fa-play");
	});

	audio.addEventListener("loadedmetadata", () =>
	{
		durationTime.innerText = new Runtime(0, audio.duration).format().text;
	});

	audio.addEventListener("timeupdate", () =>
	{
		updateSeekbar();

		elapsedTime.innerText = new Runtime(0, audio.currentTime).format().text;
	});

	seekbar.addEventListener("click", (event) =>
	{
		const time = Math.floor(audio.duration * (event.clientX / seekbar.clientWidth));

		audio.currentTime = time;
	});

	back10sControl.addEventListener("click", () =>
	{
		let newTime = Math.max(0, audio.currentTime - 10);

		audio.currentTime = newTime;
	});

	playControl.addEventListener("click", () =>
	{
		if (isNaN(audio.duration) || audio.duration == 0)
		{
			return;
		}

		if (audio.paused)
		{
			audio.play();
			playControlIcon.classList.remove("fa-play");
			playControlIcon.classList.add("fa-pause");
		}
		else
		{
			audio.pause();
			playControlIcon.classList.remove("fa-pause");
			playControlIcon.classList.add("fa-play");
		}
	});

	forward10sControl.addEventListener("click", () =>
	{
		let newTime = Math.min(audio.duration, audio.currentTime + 10);

		audio.currentTime = newTime;
	});

	play = (playInfo) =>
	{
		audio.replaceChildren(new DE("source",
			{
				src: playInfo.chapter.stream.url,
				type: "audio/mpeg",
			}).renderToHTMLElement());

		audio.load();

		workTitleElement.replaceChildren(Anchor(
			{
				href: "/podfics/view/" + playInfo.work.id,
				text: playInfo.work.title,
			}).renderToHTMLElement());

		workAuthorsElement.replaceChildren(...
			[
				document.createTextNode("by "),

				...playInfo.work.authors.map((author) => Anchor(
					{
						href: "/contributors/view/" + author.id,
						text: author.name,
					}).renderToHTMLElement()),
			]);

		if (playInfo.work.totalChapters == 1)
		{
			chapterNumberAndTitleElement.classList.remove("show");
		}
		else
		{
			chapterNumberAndTitleElement.replaceChildren(Anchor(
				{
					href: "/podfics/view/" + playInfo.work.id + "/chapters/" + playInfo.chapter.number,
					text:
					[
						"Chapter ",
						playInfo.chapter.number,

						playInfo.chapter.title != null
							? [
								": ",
								playInfo.chapter.title,
							]
						: null,
					],
				}).renderToHTMLElement());

			chapterNumberAndTitleElement.classList.add("show");
		}

		// TODO: add extra margin to the bottom of the site?

		element.classList.add("show");
 
		audio.play();
		playControlIcon.classList.remove("fa-play");
		playControlIcon.classList.add("fa-pause");
	};

	element.classList.add("initialised");
}

async function initialisePlayButton(element: HTMLElement)
{
	const button = getElementOrThrow<HTMLButtonElement>(element, "button");

	const workId = getStringDataOrThrow(button, "workId");
	const workTitle = getStringDataOrThrow(button, "workTitle");
	const workAuthorIds = getStringDataOrThrow(button, "workAuthorIds").split(",");
	const workAuthorNames = getStringDataOrThrow(button, "workAuthorNames").split(",");
	const workTotalChapters = getIntegerData(button, "workTotalChapters");
	
	const chapterTitle = getStringData(button, "chapterTitle");
	const chapterNumber = getIntegerDataOrThrow(button, "chapterNumber");
	const chapterRuntimeSeconds = getIntegerDataOrThrow(button, "chapterRuntimeSeconds");
	const chapterStreamUrl = getStringDataOrThrow(button, "chapterStreamUrl");

	const workAuthors: PlayInfo["work"]["authors"] = [];

	for (const [ index, id ] of workAuthorIds.entries())
	{
		const name = workAuthorNames[index] ?? "";

		workAuthors.push({ id, name });
	}

	const playInfo: PlayInfo =
	{
		work:
		{
			id: workId,
			title: workTitle,
			authors: workAuthors,
			totalChapters: workTotalChapters,
		},
		chapter:
		{
			title: chapterTitle,
			number: chapterNumber,
			runtime: new Runtime(0, chapterRuntimeSeconds),
			stream:
			{
				url: chapterStreamUrl,
			},
		},
	};

	button.addEventListener("click", () => play(playInfo));

	element.classList.add("initialised");
}

//
// Component
//

export async function initialiseGlobalAudioPlayers()
{
	const elements = document.querySelectorAll<HTMLElement>(
		".component-global-audio-player:not(.initialised)",
	);

	for (const element of elements)
	{
		initialise(element)
			.then(() => console.log("[GlobalAudioPlayer] Initialised:", element))
			.catch((error) => console.error(
				"[GlobalAudioPlayer] Error initialising:", element, error,
			));
	}
}

export async function initialiseGlobalAudioPlayerPlayButtons()
{
	const elements = document.querySelectorAll<HTMLElement>(
		".component-global-audio-player-play-button:not(.initialised)",
	);

	for (const element of elements)
	{
		initialisePlayButton(element)
			.then(() => console.log("[GlobalAudioPlayer] Initialised play button:", element))
			.catch((error) => console.error(
				"[GlobalAudioPlayer] Error initialising play button:", element, error,
			));
	}
}